import { useTranslation } from 'react-i18next';
import { ActionIcon, Divider, Group, Paper, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconCircleCheck, IconDownload, IconFileText } from '@tabler/icons-react';
import DealPrice from '@/components/app/deals/DealPrice';
import NoNds from '@/components/common/NoNds';
import { useDownloadDocument } from '@/hooks/useDeals';
import { buildSettlement } from '@/lib/dealPrice';
import { formatMoney } from '@/lib/format';
import type { BargainDocument, BargainStepsInfo } from '@/types/deals';

interface DealFinalSettlementProps {
  readonly info: BargainStepsInfo;
}

// Документы окончательного расчёта: счёт на доплату / требование на возврат / УКД / итоговая справка-расчёт.
const SETTLEMENT_DOC_TYPES = new Set([
  'UNDERPAYMENT_INVOICE_TEMPLATE',
  'SIGNED_UNDERPAYMENT_INVOICE',
  'REFUND_REQUIREMENT_TEMPLATE',
  'SIGNED_REFUND_REQUIREMENT',
  'UKD_TEMPLATE',
  'SIGNED_UKD',
  'FINAL_REF',
]);

// Окончательный расчёт (read-only содержимое шага): итог по факту поставки (доплата/переплата/закрыто,
// формулировка по роли) + документы расчёта + полная разбивка цены (DealPrice). Используется и в живом
// шаге (FinalPaymentStep), и в read-only ревизии завершённой сделки (DealStepReadonly).
export default function DealFinalSettlement({ info }: DealFinalSettlementProps) {
  const { t } = useTranslation();
  const download = useDownloadDocument();
  const settlement = buildSettlement(info);
  // Формулировка расчёта зависит от аудитории: продавец/покупатель — от первого лица («вам»),
  // агент/банк — нейтрально (наблюдатель, деньги не его).
  const audience: SettlementAudience =
    info.isSeller === true ? 'seller' : info.userRole === 'AGENT' || info.userRole === 'BANK' ? 'observer' : 'buyer';
  const rub = t('app.deals.rub');

  const docs = (info.documents ?? []).filter((d) => d.documentType && SETTLEMENT_DOC_TYPES.has(d.documentType));

  return (
    <Stack gap="lg">
      {settlement ? (
        <Paper withBorder radius="md" p="md" bg="gray.0">
          <Group justify="space-between" wrap="nowrap" align="center" gap="md">
            <Group gap="sm" wrap="nowrap" align="center">
              <ThemeIcon
                size={32}
                radius="xl"
                variant="light"
                color={settlement.kind === 'settled' ? 'lime' : 'gray'}
              >
                {settlement.kind === 'underpayment' ? (
                  <IconArrowUp size={18} />
                ) : settlement.kind === 'overpayment' ? (
                  <IconArrowDown size={18} />
                ) : (
                  <IconCircleCheck size={18} />
                )}
              </ThemeIcon>
              <Text fz="sm" fw={500}>
                {t(settlementLabelKey(settlement.kind, audience))}
              </Text>
            </Group>
            {settlement.kind !== 'settled' ? (
              <Text fz="lg" fw={600}>
                {formatMoney(settlement.amount)} {rub}
                <NoNds />
              </Text>
            ) : null}
          </Group>
        </Paper>
      ) : null}

      {docs.length > 0 ? (
        <Stack gap="xs">
          <Text fz="sm" fw={500} c="gray.8">
            {t('app.deals.finalPayment.settlementDocs')}
          </Text>
          {docs.map((doc) => (
            <DocRow key={doc.id ?? doc.name} doc={doc} downloading={download.isPending && download.variables === doc.id} onDownload={() => doc.id != null && download.mutate(doc.id)} t={t} />
          ))}
        </Stack>
      ) : null}

      {(settlement || docs.length > 0) && <Divider />}

      <DealPrice info={info} />
    </Stack>
  );
}

// Аудитория формулировки расчёта: продавец и покупатель — от первого лица, агент/банк — нейтрально.
type SettlementAudience = 'seller' | 'buyer' | 'observer';

function settlementLabelKey(kind: 'underpayment' | 'overpayment' | 'settled', audience: SettlementAudience): string {
  const p = 'app.deals.finalPayment.';
  if (kind === 'settled') {
    return `${p}settleClosed`;
  }
  if (kind === 'underpayment') {
    return audience === 'seller' ? `${p}settleUnderSeller` : audience === 'observer' ? `${p}settleUnderObserver` : `${p}settleUnderBuyer`;
  }
  return audience === 'seller' ? `${p}settleOverSeller` : audience === 'observer' ? `${p}settleOverObserver` : `${p}settleOverBuyer`;
}

function DocRow({
  doc,
  downloading,
  onDownload,
  t,
}: {
  readonly doc: BargainDocument;
  readonly downloading: boolean;
  readonly onDownload: () => void;
  readonly t: (key: string) => string;
}) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <ThemeIcon size={28} radius="md" variant="light" color="gray">
          <IconFileText size={16} />
        </ThemeIcon>
        <Text fz="sm" lineClamp={1}>
          {doc.name ?? t('app.deals.docs.untitled')}
        </Text>
      </Group>
      {doc.id != null ? (
        <Tooltip label={t('app.deals.docs.download')}>
          <ActionIcon variant="subtle" color="gray" aria-label={t('app.deals.docs.download')} loading={downloading} onClick={onDownload}>
            <IconDownload size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
}
